/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef } from 'preact/hooks';

import { Keys, containsKey, addKey, removeKey } from '../utils/UNSAFE_keys';
import { SelectionDetail } from '../UNSAFE_Collection';
import { styles } from './themes/SelectorStyles.css';
import { StyledCheckbox } from '../PRIVATE_StyledCheckbox';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

/**
 * Props for the Selector Component
 */
export type Props<K> = {
  /**
   * The key associated with the Selector.
   */
  rowKey: K;
  /**
   * The selected keys.
   */
  selectedKeys: Keys<K>;
  /**
   * aria-label for this selector
   */
  'aria-label'?: string;
  /**
   * Determine whether to show the visual partial icon when selector is checked. If the
   * selector is checked and isPartial is set to true, the selector would show a partially
   * checked icon.
   */
  isPartial?: boolean;
  /**
   * Specifies the selection mode. For selection mode 'all', please refer to SelectorAll component.
   */
  selectionMode?: 'multiple' | 'single';
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
  /**
   * One or more ids (separated by spaces) of elements that label the selected item. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;
};

/**
 * The Selector component renders checkboxes in collections to support selection.
 */
export function Selector<K>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  rowKey,
  selectedKeys,
  isPartial,
  selectionMode = 'multiple',
  onChange,
  testId
}: Props<K> & TestIdProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isActive, activeProps } = useActive();
  const { isFocus, focusProps } = useFocus();
  const { onKeyUp: onActiveKeyUp, ...activeRest } = activeProps;

  const onSelectionHandler = useCallback(
    (event: KeyboardEvent | MouseEvent) => {
      const { key } = event as KeyboardEvent;
      if (key !== undefined && key !== ' ') {
        return;
      }

      handleSelect(event, rowKey, selectedKeys, selectionMode, onChange);
    },
    [rowKey, selectedKeys, selectionMode, onChange]
  );

  // because we stop event propagation on selection, we must ensure
  // that the event gets passed to useActive's onKeyUp handler first
  // so that it doesn't get stuck in "active" mode
  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent) => {
      onActiveKeyUp(e);
      onSelectionHandler(e);
    },
    [onActiveKeyUp, onSelectionHandler]
  );

  const onClick = useCallback(
    (e: MouseEvent) => {
      inputRef.current?.focus();
      onSelectionHandler(e);
    },
    [onSelectionHandler]
  );

  const isChecked = isPartial
    ? 'partial'
    : containsKey(selectedKeys, rowKey)
    ? 'checked'
    : 'unchecked';

  const testIdProps = useTestId(testId);

  return (
    <div
      class={styles.base}
      onClick={onClick}
      onKeyUp={onKeyUpHandler}
      {...activeRest}
      {...testIdProps}>
      <StyledCheckbox
        ref={inputRef}
        isActive={isActive}
        isChecked={isChecked}
        isFocusRingShown={isFocus}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        onKeyDown={onKeyDown}
        {...focusProps}
      />
    </div>
  );
}

const handleSelect = <K,>(
  event: MouseEvent | KeyboardEvent,
  rowKey: K,
  selectedKeys: Props<K>['selectedKeys'],
  selectionMode: Props<K>['selectionMode'],
  onChange?: Props<K>['onChange']
) => {
  if (onChange) {
    const newKeys = getNewKeys(rowKey, selectedKeys, selectionMode);
    onChange({ value: newKeys, target: event.target });

    // we don't want container component like ListView to process the click event,
    // and we also stop the event propagation in keyup due to issue in JET-60156
    event.stopPropagation();
  }
};

const getNewKeys = <K,>(
  rowKey: K,
  selectedKeys: Props<K>['selectedKeys'],
  selectionMode: Props<K>['selectionMode']
) => {
  let newKeys;
  const checked = containsKey(selectedKeys, rowKey);
  if (checked) {
    newKeys = removeKey(selectedKeys, rowKey, false);
  } else {
    if (selectionMode === 'single') {
      newKeys = { all: false, keys: new Set([rowKey]) } as Keys<K>;
    } else {
      newKeys = addKey(selectedKeys, rowKey);
    }
  }
  return newKeys;
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key == ' ') {
    // prevent auto scrolling
    event.preventDefault();
  } else if (event.key == 'Enter') {
    // prevent active styles applied to checkbox
    event.stopPropagation();
  }
};
